import { defineMessages, type MessageDescriptor } from '@kyruus/intl';

/**
 * Internationalized messages for this widget
 * These get passed through `React-Intl`
 * And should follow a format of:
 * Roses are {color_1} and violets are {color_2}
 */

const messages = defineMessages<
  string,
  MessageDescriptor,
  Record<string, MessageDescriptor>
>({
  // ProviderProfileNav
  profileNavAriaLabel: {
    // Used by original provider profile
    id: 'provider.profile.nav.aria_label',
    description: 'Accessibility label for the profile navigation.',
    defaultMessage: 'Profile Navigation'
  },
  highlights: {
    id: 'provider.profile.nav.highlights',
    description:
      "Text for the navigation hyperlink leading to the 'Highlights' section.",
    defaultMessage: 'Highlights'
  },
  highlightsAriaLabel: {
    id: 'provider.profile.nav.highlights.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Highlights' section.",
    defaultMessage: 'Highlights section'
  },
  aboutNavTitle: {
    // Used by original provider profile
    id: 'provider.profile.panel.about',
    description:
      "Text for the navigation hyperlink leading to the 'About' section.",
    defaultMessage: 'About'
  },
  aboutNavAriaLabel: {
    id: 'provider.profile.panel.about.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'About' section.",
    defaultMessage: 'About section'
  },
  appointmentsNavTitle: {
    id: 'provider.profile.panel.appointments',
    description:
      "Text for the navigation hyperlink leading to the 'Appointments' Section",
    defaultMessage: 'Appointments section'
  },
  appointmentsNavAriaLabel: {
    id: 'provider.profile.panel.appointments.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Appointments' section.",
    defaultMessage: 'Appointments section'
  },
  videosNavTitle: {
    id: 'provider.profile.nav.videos',
    description:
      "Text for the navigation hyperlink leading to the 'Video' Section",
    defaultMessage: 'Video'
  },
  videosNavAriaLabel: {
    id: 'provider.profile.nav.videos.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Video' section.",
    defaultMessage: 'Video section'
  },
  locationsNavTitle: {
    id: 'provider.profile.nav.locations',
    description:
      "Text for the navigation hyperlink leading to the 'Locations' Section",
    defaultMessage: 'Locations'
  },
  locationsNavAriaLabel: {
    id: 'provider.profile.nav.locations.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Locations' section.",
    defaultMessage: 'Locations section'
  },
  clinicalInterestsNavTitle: {
    // Used by original provider profile
    id: 'provider.profile.nav.clinicalInterests',
    description:
      "Text for the navigation hyperlink leading to the 'Clinical Interests' Section",
    defaultMessage: 'Clinical Interests'
  },
  clinicalInterestsNavAriaLabel: {
    id: 'provider.profile.nav.clinicalInterests.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Clinical Interests' section.",
    defaultMessage: 'Clinical Interests section'
  },
  expertiseNavTitle: {
    // Used by original provider profile
    id: 'provider.profile.panel.experience',
    description:
      "Text for the navigation hyperlink leading to the 'Expertise' Section",
    defaultMessage: 'Expertise'
  },
  expertiseNavAriaLabel: {
    id: 'provider.profile.panel.experience.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Expertise' section.",
    defaultMessage: 'Expertise section'
  },
  insuranceNavTitle: {
    // Used by original provider profile
    id: 'provider.profile.panel.insurance',
    description:
      "Text for the navigation hyperlink leading to the 'Insurance' Section",
    defaultMessage: 'Insurance'
  },
  insuranceNavAriaLabel: {
    id: 'provider.profile.panel.insurance.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Insurance' section.",
    defaultMessage: 'Insurance section'
  },
  reviewsNavTitle: {
    // Used by original provider profile
    id: 'provider.profile.panel.reviews',
    description:
      "Text for the navigation hyperlink leading to the 'Reviews' Section",
    defaultMessage: 'Reviews'
  },
  reviewsNavAriaLabel: {
    id: 'provider.profile.panel.reviews.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Reviews' section.",
    defaultMessage: 'Reviews section'
  },
  careTeamNavTitle: {
    id: 'provider.profile.panel.careTeam',
    description:
      "Text for the navigation hyperlink leading to the 'Care Team' Section",
    defaultMessage: 'Care Team'
  },
  careTeamNavAriaLabel: {
    id: 'provider.profile.panel.careTeam.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Care Team' section.",
    defaultMessage: 'Care Team section'
  },
  altProvidersNavTitle: {
    id: 'provider.profile.panel.altProviders',
    description:
      "Text for the navigation hyperlink leading to the 'Similar Providers' Section",
    defaultMessage: 'Similar Providers'
  },
  altProvidersNavAriaLabel: {
    id: 'provider.profile.panel.altProviders.aria_label',
    description:
      "Accessibility label for the navigation hyperlink leading to the 'Similar Providers' section.",
    defaultMessage: 'Similar Providers section'
  },

  // ProviderBooking
  // ===================
  bookingHeader: {
    id: 'provider.profile.header.booking',
    description: 'Header for the booking section',
    defaultMessage: 'Schedule Appointment'
  },
  availability: {
    id: 'providerlist.badge.availability',
    description:
      'Badge displaying that the provider has available appointments within a certain number of days',
    defaultMessage: `Appointments available {days, plural,
      =0 {today}
      one {within # day}
      other {within # days}
    }`
  },
  newPatient: {
    id: 'provider.profile.apptInfo.newPatient',
    description: 'Text for labelling that the patient relationship is new',
    defaultMessage: 'New'
  },
  establishedPatient: {
    id: 'provider.profile.apptInfo.establishedPatient',
    description:
      'Text for labelling that the patient relationship is established',
    defaultMessage: 'Established'
  },
  patientRelationshipLabel: {
    id: 'provider.profile.apptInfo.patientRelationship',
    description: 'Label for the patient relationship buttons',
    defaultMessage: 'Patient Type'
  },
  appointmentPurposeLabel: {
    id: 'provider.profile.apptInfo.appointmentPurpose',
    description: 'Label for the appointment purpose dropdown',
    defaultMessage: 'Appointment Type'
  },
  visitMethodLabel: {
    id: 'provider.profile.apptInfo.visitMethod',
    description: 'Label for the visit method buttons',
    defaultMessage: 'Visit Method'
  },
  appointmentPurposePlaceholder: {
    id: 'provider.profile.apptInfo.appointmentPurposePlaceholder',
    description: 'Placeholder for the appointment purpose dropdown',
    defaultMessage: 'Select an appointment type'
  },
  visitTypeInPerson: {
    id: 'provider.profile.apptInfo.visit_type_in_person',
    description: 'Text to be displayed for a related purpose option',
    defaultMessage: 'In Person Visit'
  },
  visitTypeVideo: {
    id: 'provider.profile.apptInfo.visit_type_video',
    description: 'Text to be displayed for a related purpose option',
    defaultMessage: 'Video Visit'
  },
  visitTypePhone: {
    id: 'provider.profile.apptInfo.visit_type_phone',
    description: 'Text to be displayed for a related purpose option',
    defaultMessage: 'Phone Visit'
  },
  visitGroupInPerson: {
    id: 'provider.profile.apptInfo.visit_group_in_person',
    description: 'Text to be displayed for the in person purpose group',
    defaultMessage: 'In Person'
  },
  visitGroupVirutal: {
    id: 'provider.profile.apptInfo.visit_group_virtual',
    description: 'Text to be displayed for the virtual purpose group',
    defaultMessage: 'Virtual'
  },
  disabledPatientRelationshipWarning: {
    id: 'provider.profile.apptInfo.disabledPatientRelationshipWarning',
    description:
      'Text to be displayed when a patient relationship option is disabled',
    defaultMessage:
      'No online appointments available for {relationship} patients, call to schedule.'
  },
  profileContactWithPhoneOnly: {
    id: 'provider.profile.scheduling.phoneOnly',
    description:
      'Text for help in scheduling call when only phone exists but not request URL',
    defaultMessage: 'Need help scheduling? Call {phone}'
  },
  newPatientWarningBannerLabel: {
    id: 'provider.profile.apptInfo.newPatientWarningBannerLabel',
    description:
      'Text for warning banner labelling that the patient relationship is new',
    defaultMessage: 'New'
  },
  establishedPatientWarningBannerLabel: {
    id: 'provider.profile.apptInfo.establishedPatientWarningBannerLabel',
    description:
      'Text for warning banner labelling that the patient relationship is established',
    defaultMessage: 'Established'
  },
  ariaLiveLoadingBookingInformation: {
    id: 'aria.live.loading-booking-information',
    description: 'Aria live message for loading booking information',
    defaultMessage: `Loading booking information`
  },
  ariaLiveLoadingBookingInformationComplete: {
    id: 'aria.live.loading-booking-information',
    description: 'Aria live message for loading booking information',
    defaultMessage: `Loading booking information complete`
  },

  // ProviderNetworkAffiliations
  // ===================
  profileNetworkAffiliationsHeader: {
    id: 'field.name.provider.network_affiliations.name',
    description:
      "Title for a section listing a provider's network affiliations",
    defaultMessage: 'Affiliations'
  },

  // ProviderAppointments
  // ===================
  profileAppointmentsHeader: {
    id: 'provider.profile.appointments.heading',
    description: "Title for a section listing a provider's appointments",
    defaultMessage: 'Appointments'
  },
  openSchedulingWidgetLabel: {
    id: 'provider.profile.appointments.open_scheduling.label',
    description: 'Label for the open scheduling widget',
    defaultMessage: 'MyChart Open Scheduling Widget'
  },

  // ProviderInformationCards
  // ===================
  telehealthHeader: {
    id: 'infocard.telehealth.header',
    description: 'Header for the telehealth information card',
    defaultMessage: 'Virtual Care'
  },
  telehealthText: {
    id: 'infocard.telehealth.text',
    description: 'Text for the telehealth information card',
    defaultMessage: 'Offers Video Visits'
  },
  languagesHeader: {
    id: 'infocard.languages.header',
    description: 'Header for the languages information card',
    defaultMessage: 'Languages'
  },
  ageGroupsSeenHeader: {
    id: 'infocard.age_groups_seen.header',
    description: 'Header for the age groups seen information card',
    defaultMessage: 'Age Groups Seen'
  },
  genderHeader: {
    id: 'infocard.gender.header',
    description: 'Header for the gender information card',
    defaultMessage: 'Gender'
  },
  insurancePlansUnfacetedHeader: {
    id: 'infocard.insurance_plans.unfaceted_header',
    description: 'Header for the insurance plans information card',
    defaultMessage: 'In-Network Plans'
  },
  informationCardFilterMatchIconAriaLabel: {
    id: 'infocard.filter_match_icon.aria_label',
    description:
      'Accessibility label for the information card filter match icon',
    defaultMessage: 'Filter match icon'
  },
  informationCardFilterMatchClearButtonAriaLabel: {
    id: 'infocard.filter_match_clear_button.aria_label',
    description:
      'Accessibility label for the information card filter match clear button',
    defaultMessage: 'Clear filter match icon'
  },
  informationCardFilterMatchClearButtonText: {
    id: 'infocard.filter_match_clear_button.text',
    description: 'Information card filter match clear button text',
    defaultMessage: 'Clear'
  },
  informationCardMoreTooltipText: {
    id: 'infocard.more_tooltip.text',
    description:
      'Text value for the tooltip that displays when information card items are cut off',
    defaultMessage: '{itemsCount} {tooltipText}'
  },
  informationCardTooltipTextMore: {
    id: 'infocard.more_tooltip.more',
    description: 'Text value "more" to be shown along-with count',
    defaultMessage: 'more'
  },
  informationCardTooltipTextItems: {
    id: 'infocard.more_tooltip.items',
    description: 'Text value "items" to be shown along-with count',
    defaultMessage: 'items'
  },
  ariaMoreTooltipText: {
    id: 'infocard.more_tooltip.aria_label.text',
    description: 'Text value for the tooltip that displays on Voice Over',
    defaultMessage: '{itemLength} more: {items}'
  },
  informationCardInsurancesViewAll: {
    id: 'infocard.insurances.view_all',
    description: 'View all insurances',
    defaultMessage: 'View All Accepted Plans ({numInsurances})'
  },

  // ProviderAbout
  // ===================
  profileAboutHeader: {
    id: 'provider.profile.panel.aboutprovider',
    description: 'Title for a section giving information about a provider',
    defaultMessage: 'About'
  },
  profileAbout_philosophy_of_care: {
    id: 'field.name.philosophy_of_care',
    description: "Title for the provider's philosophy of care",
    defaultMessage: 'Philosophy of Care'
  },
  profileAbout_provider_title: {
    id: 'field.name.provider_title',
    description: "Title for the provider's titles",
    defaultMessage: 'Titles'
  },
  profileAbout_publications: {
    id: 'field.name.publications',
    description: "Title for the provider's publications",
    defaultMessage: 'Publications'
  },
  profileAbout_hmh_healthu_article_link: {
    id: 'field.name.hmh_healthu_article_link',
    description: "Title for the provider's HMH HealthU Articles",
    defaultMessage: 'HMH HealthU Articles'
  },
  profileAbout_podcast_link: {
    id: 'field.name.podcast_link',
    description: "Title for the provider's Podcasts, Webinars and Events",
    defaultMessage: 'Podcasts, Webinars and Events'
  },
  profileAbout_preferred_name: {
    id: 'field.name.preferred_name',
    description: "Title for the provider's preferred name",
    defaultMessage: 'Preferred Name'
  },
  profileAbout_professional_statement: {
    id: 'field.name.professional_statement',
    description: "Title for the provider's professional statement",
    defaultMessage: 'Professional Statement'
  },
  profileAbout_clinical_trials_text: {
    id: 'field.name.clinical_trials_text',
    description: "Title for the provider's clinical trials",
    defaultMessage: 'Clinical Trials'
  },
  profileAbout_awards: {
    id: 'field.name.awards',
    description: "Title for the provider's awards",
    defaultMessage: 'Awards'
  },
  profileAbout_research_text: {
    id: 'field.name.research_text',
    description: "Title for the provider's research text",
    defaultMessage: 'Research Text'
  },
  profileAbout_personal_interests: {
    id: 'field.name.personal_interests',
    description: "Title for the provider's personal interests",
    defaultMessage: 'Personal Interests'
  },
  profileAbout_special_program: {
    id: 'field.name.special_program',
    description: "Title for the provider's special program",
    defaultMessage: 'Special Program'
  },
  profileAbout_leadership_title: {
    id: 'field.name.leadership_title',
    description: "Title for the provider's leadership title",
    defaultMessage: 'Leadership Title'
  },
  profileAbout_publication_text: {
    id: 'field.name.publication_text',
    description: "Title for the provider's publication text",
    defaultMessage: 'Publication Text'
  },
  profileAbout_academic_appointment: {
    id: 'field.name.academic_appointment',
    description: "Title for the provider's academic appointment",
    defaultMessage: 'Academic Appointment'
  },
  profileAbout_clinical_special_interests: {
    id: 'field.name.clinical_special_interests',
    description: "Title for the provider's clinical special interests",
    defaultMessage: 'Clinical Interests'
  },
  // ProviderClinicalInterests
  // ===================
  providerClinicalInterestsTitle: {
    id: 'clinical.interests.title',
    description:
      "Title the Clinical Interests section of the provider's profile",
    defaultMessage: 'Clinical Interests'
  },

  // ProviderExpertise
  // ===================
  providerExpertiseTitle: {
    id: 'expertise.title',
    description: "Title the Expertise section of the provider's profile",
    defaultMessage: 'Expertise'
  },
  educationSectionTitle: {
    id: 'expertise.education.title',
    description: "Title for the Education section of the provider's expertise",
    defaultMessage: 'Education'
  },
  boardCertificationsSectionTitle: {
    id: 'expertise.board_certifications.title',
    description:
      "Title for the Board Certifications section of the provider's expertise",
    defaultMessage: 'Board Certifications'
  },
  clinicalTrialsSectionTitle: {
    id: 'expertise.clinical_trials.title',
    description:
      "Title for the Clinical Trials section of the provider's expertise",
    defaultMessage: 'Clinical Trials'
  },

  // ProviderLocation
  // ===================
  providerLocationsHeader: {
    id: 'provider.profile.header.locations',
    description: "Header for the Locations section of the provider's profile",
    defaultMessage: 'Locations'
  },
  staticMapLabel: {
    id: 'staticMap.label',
    description: 'aria label for the static map',
    defaultMessage: 'Street Map'
  },
  getDirections: {
    id: 'map_card.directionslink',
    description:
      "Label for a link that links to directions to the provider's office",
    defaultMessage: 'Get Directions'
  },
  closed: {
    id: 'location.hours.closed',
    description: 'provider location marketable location office closed text',
    defaultMessage: 'Closed'
  },
  openUntil: {
    id: 'locations.openUntil',
    description:
      'provider location marketable location office open until today',
    defaultMessage: 'Open until {openUntilTime}'
  },
  opensNext: {
    id: 'locations.openNext',
    description: 'provider location marketable location office next open time',
    defaultMessage: 'Opens {nextAvailDay} {nextAvailTime}'
  },
  openNextToday: {
    id: 'locations.openNextToday',
    description:
      'provider location marketable location office next open time today',
    defaultMessage: 'Open Next {openNextTime}'
  },
  acceptingNewPatientMessage: {
    id: 'providerlist.accepting_new_patient_message',
    description: 'Message to display if the provider is accepting new patients',
    defaultMessage: 'Accepting New Patients'
  },

  // ProviderInsuranceAccepted
  // ===================
  insuranceDisclaimerMessage: {
    id: 'provider.profile.insurancedisclaimer.message',
    description:
      "The message for the provider's medical insurance disclaimer message",
    defaultMessage:
      'Please contact the location directly to confirm your health plan is accepted. '
  },
  insuranceComponentTitle: {
    id: 'provider.profile.insurance.title',
    description:
      'The title passed to the component used to render the insurances',
    defaultMessage: 'Insurance'
  },
  insuranceComponentSearchTitle: {
    id: 'provider.profile.insurance.search.title',
    description: 'The title for the insurance search input',
    defaultMessage: 'Search plans'
  },
  insuranceComponentSearchAriaLabelItemType: {
    id: 'provider.profile.insurance.search.itemType',
    description:
      'The item type for rendering the aria label for the search input',
    defaultMessage: 'insurances accepted'
  },

  // ProviderReviews
  // ===================
  reviewsTitle: {
    id: 'provider.profile.panel.ratingsandreviews',
    description: "Title for the Reviews section of the provider's profile",
    defaultMessage: 'Reviews'
  },
  reviewsSearchInputTitle: {
    id: 'provider.profile.reviews.searchInputTitle',
    description: 'Text for the Reviews section search input title',
    defaultMessage: 'Search reviews'
  },
  ratingReviewCount: {
    id: 'provider.profile.ratings.ratingreviewcount',
    description: 'The number of ratings and reviews that the provider has',
    defaultMessage:
      '{ratingCount} {ratingCount, plural, one {Rating} other {Ratings}}, {reviewCount} {reviewCount, plural, one {Review} other {Reviews}}'
  },
  ratingsDisclaimer: {
    id: 'provider.profile.ratingsdisclaimer',
    description: "Disclaimer text for where the provider's reviews originate",
    defaultMessage:
      'The Patient Satisfaction Rating is an average of all responses to the care provider related questions shown below from our survey. Patients that are treated in outpatient or hospital environments may receive different surveys, and the volume of responses will vary by question. '
  },
  ariaStarRatingsAverage: {
    id: 'aria.label.starratings.average',
    description: "Accessibility label identifying the provider's star ratings",
    defaultMessage: 'Average provider rating of {averageRating} out of 5 stars'
  },
  ariaStarRatingsIndividualReview: {
    id: 'aria.label.starratings.individual',
    description:
      "Accessibility label identifying the provider's individual review star ratings",
    defaultMessage: '{rating} out of 5 stars'
  },
  ariaMetricStarRatingsAverage: {
    id: 'aria.label.metric.starratings.average',
    description:
      "Accessibility label identifying the provider's metrics average star ratings",
    defaultMessage: 'Average provider {metric} rating {rating} out of 5 stars'
  },
  ratingScale: {
    id: 'provider.profile.ratings.scale',
    description: 'Text that specifies the rating scale',
    defaultMessage: 'of 5 stars'
  },
  noReviews: {
    id: 'provider.profile.reviews.noreviews',
    description: 'Informs the user there are no reviews available',
    defaultMessage: 'No Reviews Available'
  },
  reviewsReviewDate: {
    id: 'provider.profile.reviews.reviewdate',
    description: 'Message informing the user when the review is dated',
    defaultMessage: 'Reviewed on {date}'
  },
  reviewsSearchInputPlaceholder: {
    id: 'infoListBox.searchPlaceholder',
    description: 'Placeholder for the Reviews section search input',
    defaultMessage: 'Search Reviews'
  },
  reviewsSearchInputClearButton: {
    id: 'infoListBox.clearSearch',
    description: 'Clear search CTA for the Reviews section search input',
    defaultMessage: 'Clear Search'
  },
  reviewsNoMatches: {
    id: 'infoListBox.noResults',
    description: 'No matching search results message for the Reviews section',
    defaultMessage: '0 results found for “{term}”'
  },
  reviewsShowMore: {
    id: 'infoListBox.showMore',
    description: 'Show More button for the Reviews section',
    defaultMessage: 'Show More'
  },
  reviewsShowLess: {
    id: 'infoListBox.showLess',
    description: 'Show Less button for the Reviews section',
    defaultMessage: 'Show Less'
  },
  reviewNoRating: {
    id: 'provider.profile.ratings.rating.norating',
    description:
      'The text to show next to the review date for a single review that does not have a rating',
    defaultMessage: 'No Rating'
  },

  // ProviderVideo
  // ===================
  videoHeader: {
    id: 'provider.profile.video.header',
    description: 'Video section header',
    defaultMessage: 'Video'
  },
  ariavideo: {
    id: 'aria.label.provider.profile.video',
    description: "Accessibility text for a provider's video",
    defaultMessage: 'Provider video'
  },

  // Care Team
  // ===================
  careTeamTitle: {
    id: 'provider.profile.careTeam.title',
    description: 'Care Team section title',
    defaultMessage: 'Care Team'
  },
  careTeamSubtitle: {
    id: 'provider.profile.careTeam.subtitle',
    description: 'Care Team section optional subtitle',
    defaultMessage: ' '
  },
  careTeamShowMore: {
    id: 'provider.profile.careTeam.showMore',
    description: 'Show More button for the Care Team Section',
    defaultMessage: 'Show More'
  },
  careTeamShowMoreAriaLabel: {
    id: 'provider.profile.careTeam.showMoreAriaLabel',
    description:
      'ARIA label for the Show More button for the Care Team Section',
    defaultMessage: 'Show more Care Team providers'
  },
  careTeamShowLess: {
    id: 'provider.profile.careTeam.showLess',
    description: 'Show Less button for the Care Team Section',
    defaultMessage: 'Show Less'
  },
  careTeamShowLessAriaLabel: {
    id: 'provider.profile.careTeam.showLessAriaLabel',
    description:
      'ARIA label for the Show Less button for the Care Team Section',
    defaultMessage: 'Show fewer Care Team providers'
  },

  // Alternate Providers
  // ===================
  altProvidersHeader: {
    id: 'provider.profile.altProvidersHeader.header',
    description: 'Similar Providers section header',
    defaultMessage: 'Similar Providers'
  },

  // Schedule Appointment
  viewAvailabilityButtonText: {
    id: 'provider.profile.appointment.schedule.viewAvailabilityButton',
    description: 'View availability button text.',
    defaultMessage: 'View Availability'
  },
  myChartLoginButtonText: {
    id: 'provider.profile.appointment.schedule.myChartLoginButton',
    description: 'Login to MyChart button text. ',
    defaultMessage: 'Login to MyChart'
  },

  // ProviderHighlights
  // ===================
  highlightsHeader: {
    id: 'provider.profile.highlights.header',
    description: 'Highlights section header',
    defaultMessage: 'Highlights'
  },
  ariaHighlights: {
    id: 'aria.label.provider.profile.highlights',
    description: "Accessibility text for a provider's highlights",
    defaultMessage: 'Provider Highlights'
  },

  // EstimatedWaitTime
  // ===================
  estimatedWaitTimeHeader: {
    id: 'provider.profile.waitTime.header',
    description: 'Estimated wait time header',
    defaultMessage: 'Estimated Wait Time'
  },
  estimatedWaitTimeErrorMsg: {
    id: 'provider.profile.waitTime.errorMsg',
    description: 'Estimated wait time error message',
    defaultMessage:
      'Estimated Wait Time is temporarily unavailable. Please try again or refresh the page.'
  },
  externalWaitTimeLinkText: {
    id: 'provider.profile.waitTime.externalWaitTimeLinkText',
    description: 'Reserve your spot link text.',
    defaultMessage: 'Reserve Your Spot'
  },
  estimatedWaitTimeMinutes: {
    id: 'provider.profile.waitTime.minutes',
    description: 'Estimated wait time in only minutes',
    defaultMessage: '{minutes, plural, one {# minute} other {# minutes}}'
  },
  estimatedWaitTimeHoursAndMinutes: {
    id: 'provider.profile.waitTime.hoursAndMinutes',
    description: 'Estimated wait time in hours and minutes',
    defaultMessage:
      '{hours, plural, one {# hour} other {# hours}} {minutes, plural, one {# minute} other {# minutes}}'
  },
  estimatedWaitTimeHours: {
    id: 'provider.profile.waitTime.hours',
    description: 'Estimated wait time in only hours',
    defaultMessage: '{hours, plural, one {# hour} other {# hours}}'
  },

  searchBreadcrumbLink: {
    id: 'provider.profile.search_breadcrumb_link',
    description: 'Link text for search breadcrumb that returns to search page',
    defaultMessage: 'Search'
  },
  or: {
    id: 'provider.profile.navigation.or',
    description: 'Junction of multiple options',
    defaultMessage: 'or'
  },
  doctorname: {
    id: 'provider.profile.description.doctorname',
    description: "A page description for the doctor's name and title",
    defaultMessage: 'Dr. {providerName}'
  },
  basicproviderstatment: {
    id: 'provider.profile.description.statement',
    description: "Page description for the provider's profile",
    defaultMessage:
      'Find information about and book an appointment with {providerName}.'
  },
  basicproviderstatmentlocation: {
    id: 'provider.profile.description.statementlocation',
    description: "Page description for the provider's profile with locations",
    defaultMessage:
      'Find information about and book an appointment with {providerName} in {displayLocations}.'
  },
  descriptionlocations: {
    id: 'provider.profile.description.locations',
    description: "Page description for the doctor's locations",
    defaultMessage: 'Practice Locations: {displayLocations}.'
  },
  descriptionspecialties: {
    id: 'provider.profile.description.specialties',
    description: "Page description for the doctor's locations",
    defaultMessage: 'Specialties: {specialties}.'
  },
  moreAvailabilitiesLinkText: {
    id: 'availability.moreavailabilities',
    description: 'Text to display on link to view more availabilities',
    defaultMessage: 'View All Appointments'
  },
  providerAvailabilitySlotsAriaLabel: {
    id: 'availability.slotsAriaLabel',
    description:
      'Accessibility text to be read by screen reader for provider available slots',
    defaultMessage: '{date} and {time} at {locationName}'
  },
  providerAvailabilityViewMoreAriaLabel: {
    id: 'availability.viewMoreAriaLabel',
    description:
      'Accessibility text to be read by screen reader for provider view more slots',
    defaultMessage: 'view all available appointments for {providerName}'
  },
  providerProfileEditApptInfoAriaLabel: {
    id: 'availability.editApptInfoAriaLabel',
    description:
      'Accessibility text to be read by screen reader for edit appointment info',
    defaultMessage: 'Edit your appointment information'
  },
  existingPatientButtonText: {
    id: 'patientrel.existing.buttontext',
    description:
      'Button text for "existing" patientRel button when querying availability for a single provider.',
    defaultMessage: 'Yes'
  },
  newPatientButtonText: {
    id: 'patientrel.new.buttontext',
    description:
      'Button text for "new" patientRel button when querying availability for a single provider.',
    defaultMessage: 'No'
  }
});

export type MessageType = keyof typeof messages;
export default messages;
